import { FC, memo, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Box, TableBody, TableCell, TableRow, useTheme } from "@mui/material";
import { roundToTwo } from "utils/helpers";
import {
  CustomClassData,
  MCustomClassData,
  CCustomClassData,
  PCustomClassData,
  PClassEnum,
  MClassEnum,
  CClassEnum,
  MClassLavTargetValuesEnum,
  MClassUoTargetValuesEnum,
  MClassFTiTargetValuesEnum,
  MClassReiTargetValuesEnum,
  CClassEavTargetValuesEnum,
  CClassUoTargetValuesEnum,
  PClassEavTargetValuesEnum,
  PClassEminTargetValuesEnum,
} from "types";
import type { IResultItem, RawResultOutcome } from "../../types";
import { M_CLASS_SYMBOLS, C_CLASS_SYMBOLS, P_CLASS_SYMBOLS, MClassSymbol, CPClassSymbol } from "../../common/consts";
import Parameter from "../Parameter";
import getStyles from "./styles";

type Props = {
  options: IResultItem[];
  roadClasses: PClassEnum | MClassEnum | CClassEnum;
  customRoadClass: CustomClassData | undefined;
  isRoadClassM: boolean;
  isRoadClassC: boolean;
  isRoadClassP: boolean;
};

const Roadway: FC<Props> = ({ customRoadClass, options, roadClasses, isRoadClassM, isRoadClassC, isRoadClassP }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const styles = useMemo(() => getStyles(theme).Roadway, [theme]);

  const mCustomClass = useMemo(
    () => (customRoadClass?.class_type === "M" ? (customRoadClass as MCustomClassData) : undefined),
    [customRoadClass],
  );
  const cCustomClass = useMemo(
    () => (customRoadClass?.class_type === "C" ? (customRoadClass as CCustomClassData) : undefined),
    [customRoadClass],
  );
  const pCustomClass = useMemo(
    () => (customRoadClass?.class_type === "P" ? (customRoadClass as PCustomClassData) : undefined),
    [customRoadClass],
  );
  const roadwayLabel = useMemo(
    () => (customRoadClass?.class_type ? `${customRoadClass.class_type} ${t("Custom")}` : roadClasses),
    [customRoadClass?.class_type, roadClasses, t],
  );

  const getMClassValue = (symbol: MClassSymbol): string | number => {
    const mClass = roadClasses as keyof typeof MClassLavTargetValuesEnum;

    if (mCustomClass) {
      const customValues: Record<MClassSymbol, number> = {
        l_avg: mCustomClass.Lm_av,
        u_o: mCustomClass.L_uo,
        u_l: mCustomClass.L_ul,
        f_ti: mCustomClass.fTI,
        r_ei: mCustomClass.Rei,
      };
      return customValues[symbol];
    }

    const classValues: Record<MClassSymbol, string> = {
      l_avg: MClassLavTargetValuesEnum[mClass],
      u_o: MClassUoTargetValuesEnum[mClass],
      u_l: MClassUoTargetValuesEnum[mClass],
      f_ti: MClassFTiTargetValuesEnum[mClass],
      r_ei: MClassReiTargetValuesEnum[mClass],
    };

    return classValues[symbol];
  };

  const getMClassUnit = (symbol: MClassSymbol) => {
    const value = getMClassValue(symbol);
    return symbol === "l_avg" ? `${value} (cd/m2)` : `${value}`;
  };

  const getCClassValue = (symbol: CPClassSymbol): string | number | undefined => {
    const cClassEav = roadClasses as keyof typeof CClassEavTargetValuesEnum;
    const cClassUo = roadClasses as keyof typeof CClassUoTargetValuesEnum;

    if (symbol === "e_avg") {
      return cCustomClass ? cCustomClass.Em_av : CClassEavTargetValuesEnum[cClassEav];
    }

    if (symbol === "u_oe") {
      return cCustomClass ? cCustomClass.E_uo : CClassUoTargetValuesEnum[cClassUo];
    }

    return undefined;
  };

  const getCClassUnit = (symbol: CPClassSymbol) => {
    const value = getCClassValue(symbol);
    if (symbol === "u_oe") {
      return value ? `${value}` : "";
    }
    return value ? `${value} (lx)` : "(lx)";
  };

  const getPClassValue = (symbol: CPClassSymbol): string | number | undefined => {
    const pClassEav = roadClasses as keyof typeof PClassEavTargetValuesEnum;
    const pClassEmin = roadClasses as keyof typeof PClassEminTargetValuesEnum;

    if (symbol === "e_avg") {
      return pCustomClass ? pCustomClass.Em_av : PClassEavTargetValuesEnum[pClassEav];
    }

    if (symbol === "e_min") {
      return pCustomClass ? pCustomClass.Em_min : PClassEminTargetValuesEnum[pClassEmin];
    }

    return undefined;
  };

  const getPClassUnit = (symbol: CPClassSymbol) => {
    const value = getPClassValue(symbol);
    if (symbol === "u_oe") {
      return value ? `${value}` : "";
    }
    return value ? `${value} (lx)` : "(lx)";
  };

  return (
    <TableBody>
      <TableRow sx={styles.head}>
        <TableCell>
          {t("Roadway")} <strong>{roadwayLabel}</strong>
        </TableCell>
        {options.map((lum, index) => {
          const isMet = lum.RoadStandard === "met";
          const isNotMet = lum.RoadStandard === "notmet" || lum.RoadStandard === "toohigh" || lum.RoadStandard === "toohight";
          return (
            <TableCell key={"lum11" + index} align="center" scope="row">
              <Box component="strong" sx={{ color: isMet ? "var(--color-emerald-dark)" : isNotMet ? "var(--color-red)" : "inherit" }}>
                {isMet ? t("met") : isNotMet ? t("not met") : ""}
              </Box>
            </TableCell>
          );
        })}
      </TableRow>

      {isRoadClassM &&
        M_CLASS_SYMBOLS.map((symbol, index) => (
          <TableRow key={"lum12" + index}>
            <TableCell>
              <Parameter label={symbol.htmlSymbol} unit={getMClassUnit(symbol.symbol)} />
            </TableCell>
            {options.map((lum, i) => (
              <TableCell key={"lum14" + i} align="center" scope="row">
                {symbol.symbol === "f_ti"
                  ? `${Number.parseFloat((lum.result as RawResultOutcome)[symbol.symbol] as unknown as string)}%`
                  : Number.parseFloat((lum.result as RawResultOutcome)[symbol.symbol] as unknown as string).toFixed(2)}
              </TableCell>
            ))}
          </TableRow>
        ))}

      {isRoadClassC &&
        C_CLASS_SYMBOLS.map((symbol, index) => (
          <TableRow key={"lum15" + index}>
            <TableCell>
              <Parameter label={symbol.htmlSymbol} unit={getCClassUnit(symbol.symbol)} />
            </TableCell>
            {options.map((lum, i) => (
              <TableCell key={"lum20" + i} align="center" scope="row">
                {Number.parseFloat((lum.result as RawResultOutcome)[symbol.symbol] as unknown as string).toFixed(2)}
              </TableCell>
            ))}
          </TableRow>
        ))}

      {isRoadClassP &&
        P_CLASS_SYMBOLS.map((symbol, index) => (
          <TableRow key={"lum21" + index}>
            <TableCell>
              <Parameter label={symbol.htmlSymbol} unit={getPClassUnit(symbol.symbol)} />
            </TableCell>
            {options.map((lum, i) => (
              <TableCell key={"lum24" + index + i} align="center" scope="row">
                {Number.parseFloat((lum.result as RawResultOutcome)[symbol.symbol] as unknown as string).toFixed(2)}
              </TableCell>
            ))}
          </TableRow>
        ))}

      <TableRow>
        <TableCell scope="row">
          <strong>{t("TI")}</strong>
        </TableCell>
        {options.map((lum, index) => (
          <TableCell key={"TableCell" + index} align="center" scope="row">
            {lum.f_ti !== undefined && lum.f_ti !== null ? `${roundToTwo(lum.f_ti)}%` : "—"}
          </TableCell>
        ))}
      </TableRow>

      <TableRow>
        <TableCell scope="row">
          <strong>
            R<sub>UL</sub>
          </strong>
        </TableCell>
        {options.map((lum, index) => (
          <TableCell key={"TableCell" + index} align="center" scope="row">
            {lum.ulor !== undefined && lum.ulor !== null ? `${Math.round(lum.ulor)}%` : "—"}
          </TableCell>
        ))}
      </TableRow>
    </TableBody>
  );
};

const areOptionsEqual = (prevOptions: IResultItem[], nextOptions: IResultItem[]) =>
  prevOptions.length === nextOptions.length && prevOptions.every((option, index) => option === nextOptions[index]);

export default memo(Roadway, (prevProps, nextProps) => {
  return (
    prevProps.roadClasses === nextProps.roadClasses &&
    prevProps.customRoadClass === nextProps.customRoadClass &&
    prevProps.isRoadClassM === nextProps.isRoadClassM &&
    prevProps.isRoadClassC === nextProps.isRoadClassC &&
    prevProps.isRoadClassP === nextProps.isRoadClassP &&
    areOptionsEqual(prevProps.options, nextProps.options)
  );
});
